/**
 * PatentabilityPane — tab content for patentability analysis.
 *
 * Three views:
 * 1. Setup — document checklist + priority date + launch button
 * 2. Running — progress bar + growing partial results table
 * 3. Report — claim accordion with novelty per document + inventive step
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { solutionService } from '../services/solutionService';
import { usePatentabilityAnalysisStore } from '../stores/patentabilityAnalysisStore';
import type { PriorArtRelevanceEntry, Solution } from '../services/subProjectService';

interface PatentabilityPaneProps {
  subProjectId: string;
  solutionId: string;
  reloadTrigger: number;
}

// ── Verdict badge ─────────────────────────────────────────────────────────────

const VerdictBadge: React.FC<{ verdict: string }> = ({ verdict }) => {
  const v = verdict?.toLowerCase();
  if (v === 'novel') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-green-100 text-green-700">
        Novel
      </span>
    );
  }
  if (v === 'anticipated') {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700">
        Anticipated
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-500">
      {verdict || '—'}
    </span>
  );
};

// ── Progress bar ──────────────────────────────────────────────────────────────

const ProgressBar: React.FC<{ value: number; max: number }> = ({ value, max }) => {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="w-full h-1.5 bg-slate-200 rounded-full overflow-hidden">
      <div
        className="h-full bg-blue-500 transition-all duration-500"
        style={{ width: `${pct}%` }}
      />
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const PatentabilityPane: React.FC<PatentabilityPaneProps> = ({
  subProjectId,
  solutionId,
  reloadTrigger,
}) => {
  const { t } = useTranslation();
  const { progress, partialResults, report, isPolling, isLaunching, error, launch, startPolling, stopPolling, fetchReport, reset } =
    usePatentabilityAnalysisStore();

  const [solution, setSolution] = useState<Solution | null>(null);
  const [loading, setLoading] = useState(true);
  const [priorityDate, setPriorityDate] = useState('');
  const [includedDocs, setIncludedDocs] = useState<Record<string, boolean>>({});
  const [expandedClaim, setExpandedClaim] = useState<number | null>(null);

  const loadSolution = useCallback(async () => {
    setLoading(true);
    try {
      const sol = await solutionService.get(subProjectId, solutionId);
      setSolution(sol);
      if (sol.priority_target_date) setPriorityDate(sol.priority_target_date);
      // Default all docs to included
      const defaults: Record<string, boolean> = {};
      (sol.patentability_documents || []).forEach(d => {
        defaults[d.document_id] = d.is_included;
      });
      setIncludedDocs(defaults);
    } catch { /* ignore */ }
    setLoading(false);
  }, [subProjectId, solutionId]);

  useEffect(() => {
    loadSolution();
  }, [loadSolution, reloadTrigger]);

  // On unmount, stop polling
  useEffect(() => {
    return () => { stopPolling(); };
  }, [stopPolling]);

  // Auto-fetch report if status is already completed
  useEffect(() => {
    if (progress?.status === 'completed' && !report) {
      fetchReport(subProjectId, solutionId);
    }
  }, [progress?.status, report, subProjectId, solutionId, fetchReport]);

  const handleLaunch = async () => {
    reset();
    try {
      await launch(subProjectId, solutionId);
      startPolling(subProjectId, solutionId);
    } catch (_) {
      // error already in store
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-xs text-slate-400">
        {t('common.loading', 'Loading...')}
      </div>
    );
  }

  const docs: PriorArtRelevanceEntry[] = solution?.patentability_documents || [];
  // Mirror the backend (patent_analysis.py): prefer the new claims_structured,
  // fall back to the legacy claims_list.
  const structuredClaims = solution?.claims_structured || [];
  const claimsList: string[] = structuredClaims.length > 0
    ? structuredClaims.map(c => c.claim_text).filter(Boolean)
    : (solution?.claims_list || []);
  const hasDescriptionHtml = !!solution?.description_html && solution.description_html.length > 100;
  const selectedDocCount = Object.values(includedDocs).filter(Boolean).length;
  const canLaunch = claimsList.length > 0 && selectedDocCount > 0;

  const isRunning = isLaunching || isPolling || progress?.status === 'running';
  const isCompleted = progress?.status === 'completed';
  const isFailed = progress?.status === 'failed';

  // ── Running view ───────────────────────────────────────────────────────────
  if (isRunning) {
    const claimsTotal = progress?.claims_total || 0;
    const claimsProcessed = progress?.claims_processed || 0;
    const docsTotal = progress?.documents_total || 0;
    const docsProcessed = progress?.documents_processed || 0;
    const totalSteps = claimsTotal * docsTotal || 1;
    const doneSteps = claimsProcessed * docsTotal + docsProcessed;

    return (
      <div className="flex flex-col gap-4 p-4 overflow-auto h-full">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs font-semibold text-slate-700">
              {t('patentability.running', 'Analysis in progress...')}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 min-h-[1.2rem]">
            {progress?.current_step || ''}
          </div>
          {claimsTotal > 0 && (
            <div className="flex items-center gap-2 text-[11px] text-slate-500">
              <span>
                {t('patentability.claimProgress', 'Claim {{done}}/{{total}}', { done: claimsProcessed, total: claimsTotal })}
              </span>
              {docsTotal > 0 && (
                <span>· {t('patentability.docProgress', 'Document {{done}}/{{total}}', { done: docsProcessed, total: docsTotal })}</span>
              )}
            </div>
          )}
          <ProgressBar value={doneSteps} max={totalSteps} />
        </div>

        {/* Partial results table */}
        {partialResults.length > 0 && (
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1">
              {t('patentability.partialResults', 'Results so far')}
            </div>
            <div className="border border-slate-200 rounded overflow-hidden">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-2 py-1 text-left font-semibold text-slate-600 w-16">
                      {t('patentability.col.claim', 'Claim')}
                    </th>
                    <th className="px-2 py-1 text-left font-semibold text-slate-600">
                      {t('patentability.col.document', 'Document')}
                    </th>
                    <th className="px-2 py-1 text-left font-semibold text-slate-600 w-24">
                      {t('patentability.col.verdict', 'Verdict')}
                    </th>
                    <th className="px-2 py-1 text-right font-semibold text-slate-600 w-16">
                      {t('patentability.col.confidence', 'Conf.')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {partialResults.map((r, i) => (
                    <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-2 py-1 text-slate-600">#{r.claim_index}</td>
                      <td className="px-2 py-1 text-slate-600 max-w-[180px] truncate">{r.document_id}</td>
                      <td className="px-2 py-1"><VerdictBadge verdict={r.verdict} /></td>
                      <td className="px-2 py-1 text-right text-slate-500">
                        {r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Report view ────────────────────────────────────────────────────────────
  if (isCompleted && report) {
    const claimsResults: unknown[] = (report.claims_results as unknown[]) || [];

    // Count verdicts across all claims
    const novelCount = claimsResults.filter((cr: unknown) => {
      const r = cr as Record<string, unknown>;
      return r.overall_verdict === 'patentable' || r.overall_verdict === 'novel_with_inventive_step';
    }).length;
    const anticipatedCount = claimsResults.filter((cr: unknown) => {
      const r = cr as Record<string, unknown>;
      return r.overall_verdict === 'anticipated';
    }).length;
    const unclearCount = claimsResults.length - novelCount - anticipatedCount;

    return (
      <div className="flex flex-col gap-4 p-4 overflow-auto h-full">
        {/* Summary row */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-xs">
            <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold">{novelCount}</span>
            <span className="text-slate-500">{t('patentability.summary.novel', 'novel/patentable')}</span>
          </div>
          <div className="flex items-center gap-1 text-xs">
            <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 font-semibold">{anticipatedCount}</span>
            <span className="text-slate-500">{t('patentability.summary.anticipated', 'anticipated')}</span>
          </div>
          {unclearCount > 0 && (
            <div className="flex items-center gap-1 text-xs">
              <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-semibold">{unclearCount}</span>
              <span className="text-slate-500">{t('patentability.summary.unclear', 'unclear')}</span>
            </div>
          )}
          <div className="ml-auto">
            <button
              onClick={() => { reset(); loadSolution(); }}
              className="text-xs px-3 py-1 border border-slate-300 rounded text-slate-500 hover:border-blue-400 hover:text-blue-600"
            >
              {t('patentability.rerun', 'Rerun analysis')}
            </button>
          </div>
        </div>

        {/* Per-claim accordion */}
        <div className="flex flex-col gap-2">
          {claimsResults.map((cr: unknown) => {
            const claimResult = cr as Record<string, unknown>;
            const claimIdx = claimResult.claim_index as number;
            const verdict = claimResult.overall_verdict as string || '';
            const noveltyResults = (claimResult.novelty_assessments as unknown[]) || [];
            const invStep = claimResult.inventive_step as Record<string, unknown> | null;
            const isExpanded = expandedClaim === claimIdx;

            return (
              <div key={claimIdx} className="border border-slate-200 rounded overflow-hidden">
                <button
                  onClick={() => setExpandedClaim(isExpanded ? null : claimIdx)}
                  className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 text-left"
                >
                  <span className="text-xs font-semibold text-slate-700">
                    {t('patentability.claim', 'Claim')} #{claimIdx}
                  </span>
                  <div className="flex items-center gap-2">
                    <VerdictBadge verdict={verdict} />
                    <span className="text-slate-400 text-xs">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="p-3 flex flex-col gap-3">
                    {/* Novelty table */}
                    {noveltyResults.length > 0 && (
                      <div>
                        <div className="text-[11px] font-semibold text-slate-600 mb-1">
                          {t('patentability.noveltyPerDoc', 'Novelty vs. prior art')}
                        </div>
                        <div className="border border-slate-200 rounded overflow-hidden">
                          <table className="w-full text-[11px]">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-2 py-1 text-left font-semibold text-slate-600">
                                  {t('patentability.col.document', 'Document')}
                                </th>
                                <th className="px-2 py-1 text-left font-semibold text-slate-600 w-24">
                                  {t('patentability.col.verdict', 'Verdict')}
                                </th>
                                <th className="px-2 py-1 text-right font-semibold text-slate-600 w-16">
                                  {t('patentability.col.confidence', 'Conf.')}
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {noveltyResults.map((na: unknown, ni) => {
                                const n = na as Record<string, unknown>;
                                return (
                                  <tr key={ni} className="border-b border-slate-100">
                                    <td className="px-2 py-1 text-slate-600 max-w-[200px] truncate">
                                      {n.document_id as string}
                                    </td>
                                    <td className="px-2 py-1">
                                      <VerdictBadge verdict={n.is_novel ? 'novel' : 'anticipated'} />
                                    </td>
                                    <td className="px-2 py-1 text-right text-slate-500">
                                      {n.confidence != null
                                        ? `${Math.round((n.confidence as number) * 100)}%`
                                        : '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Inventive step block */}
                    {invStep && (
                      <div>
                        <div className="text-[11px] font-semibold text-slate-600 mb-1">
                          {t('patentability.inventiveStep', 'Inventive step (PSA)')}
                        </div>
                        <div className="text-[11px] text-slate-600 bg-slate-50 rounded p-2 space-y-1">
                          {!!invStep.closest_prior_art_id && (
                            <div>
                              <span className="font-semibold">{t('patentability.cpa', 'CPA')}: </span>
                              {String(invStep.closest_prior_art_id)}
                            </div>
                          )}
                          {!!invStep.objective_technical_problem && (
                            <div>
                              <span className="font-semibold">{t('patentability.otp', 'OTP')}: </span>
                              {String(invStep.objective_technical_problem)}
                            </div>
                          )}
                          {invStep.is_obvious != null && (
                            <div>
                              <span className="font-semibold">{t('patentability.obvious', 'Obvious')}: </span>
                              {invStep.is_obvious ? t('common.yes', 'Yes') : t('common.no', 'No')}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Setup view (default) ───────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 p-4 overflow-auto h-full">
      {/* Error from failed run */}
      {isFailed && progress?.error_message && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {progress.error_message}
        </div>
      )}

      {/* Store error */}
      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </div>
      )}

      {/* Warning: no claims */}
      {claimsList.length === 0 && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {t('patentability.noClaimsWarning', 'No claims found in this solution. Generate or enter claims before launching.')}
        </div>
      )}

      {/* Warning: description too short */}
      {!hasDescriptionHtml && (
        <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          {t('patentability.shortDescWarning', 'The solution description is very short. Feature analysis quality may be reduced.')}
        </div>
      )}

      {/* Priority date */}
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold text-slate-600 whitespace-nowrap">
          {t('patentability.priorityDate', 'Priority date')}
        </label>
        <input
          type="date"
          value={priorityDate}
          onChange={e => setPriorityDate(e.target.value)}
          className="text-xs border border-slate-300 rounded px-2 py-1 outline-none focus:border-blue-400"
        />
        <span className="text-[11px] text-slate-400">
          {t('patentability.priorityDateHint', '(optional — filters prior art by date)')}
        </span>
      </div>

      {/* Claims preview */}
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">
          {t('patentability.claimsLabel', 'Claims to analyse')}
        </div>
        {claimsList.length > 0 ? (
          <div className="text-[11px] text-slate-500">
            {claimsList.length} {t('patentability.claimsCount', 'claim(s) — independent ones will be analysed')}
          </div>
        ) : (
          <div className="text-[11px] text-slate-400">—</div>
        )}
      </div>

      {/* Documents checklist */}
      <div>
        <div className="text-xs font-semibold text-slate-600 mb-1">
          {t('patentability.priorArtDocs', 'Prior art documents')}
          <span className="ml-1 font-normal text-slate-400">
            ({selectedDocCount}/{docs.length} {t('patentability.selected', 'selected')})
          </span>
        </div>
        {docs.length === 0 ? (
          <div className="text-[11px] text-slate-400">
            {t('patentability.noDocuments', 'No prior art documents assigned. Add documents with patentability relevance from the Documents tab.')}
          </div>
        ) : (
          <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
            {docs.map(d => (
              <label key={d.document_id} className="flex items-start gap-2 text-[11px] text-slate-600 cursor-pointer hover:text-slate-800">
                <input
                  type="checkbox"
                  checked={includedDocs[d.document_id] ?? true}
                  onChange={e => setIncludedDocs(prev => ({ ...prev, [d.document_id]: e.target.checked }))}
                  className="mt-0.5 flex-shrink-0"
                />
                <span className="flex-1 truncate">{d.document_id}</span>
                {d.relevance_score != null && (
                  <span className="text-slate-400 flex-shrink-0">
                    {Math.round(d.relevance_score * 100)}%
                  </span>
                )}
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Launch button */}
      <div>
        <button
          onClick={handleLaunch}
          disabled={!canLaunch || isLaunching}
          className="text-xs px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLaunching
            ? t('common.loading', 'Loading...')
            : t('patentability.launch', 'Launch patentability analysis')}
        </button>
      </div>
    </div>
  );
};

export default PatentabilityPane;
